import { ReactNode, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Activity,
  Briefcase,
  Settings,
  AlertTriangle,
  Power,
  FlaskConical,
  Radio,
  History,
  RefreshCw,
  Trash2,
  LogOut,
  User,
  Cpu,
} from 'lucide-react'
import { useWebSocket } from '../hooks/useWebSocket'
import { useTradingStore } from '../hooks/useTradingStore'
import { useAuthStore } from '../hooks/useAuthStore'
import StrategySelector from './StrategySelector'
import ActiveInstrumentBadge from './ActiveInstrumentBadge'

interface LayoutProps {
  children: ReactNode
}

const navItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard, page: 'dashboard' },
  { path: '/live', label: 'Live Trading', icon: Radio, page: 'live' },
  { path: '/monitoring', label: 'Monitoring', icon: Activity, page: 'monitoring' },
  { path: '/backtest', label: 'Backtest', icon: FlaskConical, page: 'backtest' },
  { path: '/positions', label: 'Positions', icon: Briefcase, page: 'positions' },
  { path: '/settings', label: 'Settings', icon: Settings, page: 'settings' },
]

export default function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const { connectionState } = useWebSocket()
  const {
    strategyStatus, runMode, activeStrategy, emergencyClose,
    backtestHistory, backtestHistoryLoading, selectedHistoryBacktestId,
    fetchBacktestHistory, loadBacktestResult, deleteBacktestResult,
  } = useTradingStore()
  const { role, allowedPages, username, authEnabled, logout } = useAuthStore()
  const isAdmin = role === 'admin'

  const isBacktestPage = location.pathname === '/backtest'

  // Filter nav items by user's allowed pages
  const visibleNavItems = navItems.filter(item =>
    role === 'admin' || allowedPages.includes(item.page)
  )

  useEffect(() => {
    if (isBacktestPage && backtestHistory.length === 0) {
      fetchBacktestHistory()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch when entering the page, not on every history change
  }, [isBacktestPage])

  return (
    <div className="flex h-screen bg-[#0a0a0a]">
      {/* Sidebar */}
      <aside className="w-60 bg-[#111] border-r border-[#2a2a2a] flex flex-col">
        {/* Logo */}
        <div className="h-14 flex items-center px-4 border-b border-[#2a2a2a]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
              <span className="text-white font-bold text-sm">NT</span>
            </div>
            <span className="font-semibold text-white">FirstLight</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className={`${isBacktestPage ? '' : 'flex-1'} py-4`}>
          {visibleNavItems.map((item) => {
            const isActive = location.pathname === item.path
            const Icon = item.icon
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-[#1a1a1a] text-white'
                    : 'text-[#a0a0a0] hover:bg-[#1a1a1a] hover:text-white'
                }`}
              >
                <Icon size={18} />
                <span className="text-sm font-medium">{item.label}</span>
              </Link>
            )
          })}
        </nav>

        {/* Previous Backtests (when on Backtest page) */}
        {isBacktestPage && (
          <div className="border-t border-[#2a2a2a] flex flex-col min-h-0 flex-1">
            <div className="flex items-center justify-between px-4 py-2">
              <div className="flex items-center gap-1.5">
                <History size={14} className="text-blue-400" />
                <span className="text-xs font-medium text-[#a0a0a0]">Previous Backtests</span>
                <span className="text-[10px] text-[#555]">({backtestHistory.length})</span>
              </div>
              <button
                onClick={() => fetchBacktestHistory()}
                disabled={backtestHistoryLoading}
                className="p-1 text-[#555] hover:text-blue-400 transition-colors"
                title="Refresh"
              >
                <RefreshCw size={12} className={backtestHistoryLoading ? 'animate-spin' : ''} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-2 pb-2">
              {backtestHistoryLoading && backtestHistory.length === 0 ? (
                <div className="py-4 text-center text-[#555] text-xs">Loading...</div>
              ) : backtestHistory.length === 0 ? (
                <div className="py-4 text-center text-[#555] text-xs">No previous backtests</div>
              ) : (
                <div className="space-y-1">
                  {backtestHistory.map((bt) => {
                    const isSelected = selectedHistoryBacktestId === bt.backtest_id
                    const isOptimization = bt.result_type === 'optimization'
                    const isMulti = bt.result_type === 'multi_backtest'
                    return (
                      <div
                        key={bt.backtest_id}
                        className={`group rounded-md px-2.5 py-2 cursor-pointer transition-colors ${
                          isSelected
                            ? isMulti
                              ? 'bg-teal-500/10 border border-teal-500/30'
                              : isOptimization
                                ? 'bg-purple-500/10 border border-purple-500/30'
                                : 'bg-blue-500/10 border border-blue-500/30'
                            : 'hover:bg-[#1a1a1a] border border-transparent'
                        }`}
                        onClick={() => loadBacktestResult(bt.backtest_id)}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-white truncate">
                            {bt.instrument}
                          </span>
                          <div className="flex items-center gap-1">
                            {isMulti && (
                              <span className="text-[9px] px-1 py-0.5 rounded bg-teal-500/10 text-teal-400">
                                multi
                              </span>
                            )}
                            {isOptimization && (
                              <span className="text-[9px] px-1 py-0.5 rounded bg-purple-500/10 text-purple-400">
                                opt
                              </span>
                            )}
                            <span className={`text-[9px] px-1 py-0.5 rounded ${
                              bt.status === 'completed' ? 'bg-green-500/10 text-green-400' :
                              bt.status === 'error' ? 'bg-red-500/10 text-red-400' :
                              'bg-yellow-500/10 text-yellow-400'
                            }`}>
                              {bt.status === 'completed' ? 'ok' : bt.status}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-[10px] text-[#555] truncate">
                            {bt.preset_name}
                          </span>
                          {bt.summary?.pnl != null && (
                            <span className={`text-[10px] font-medium ${bt.summary.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {isOptimization ? (
                                <>best: {bt.summary.pnl >= 0 ? '+' : ''}{bt.summary.pnl.toFixed(1)}%</>
                              ) : isMulti ? (
                                <>total: {bt.summary.pnl >= 0 ? '+' : ''}${bt.summary.pnl.toFixed(2)}</>
                              ) : (
                                <>{bt.summary.pnl >= 0 ? '+' : ''}${bt.summary.pnl.toFixed(2)}</>
                              )}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center justify-between mt-0.5">
                          <span className="text-[10px] text-[#444]">
                            {new Date(bt.started_at).toLocaleDateString()}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              if (confirm('Delete this backtest result?')) {
                                deleteBacktestResult(bt.backtest_id)
                              }
                            }}
                            className="p-0.5 text-[#444] hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                            title="Delete"
                          >
                            <Trash2 size={11} />
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Connection Status */}
        <div className="p-4 border-t border-[#2a2a2a]">
          <div className="flex items-center gap-2 text-sm">
            <div className={`connection-dot ${
              connectionState === 'connected' ? 'connected' :
              connectionState === 'connecting' ? 'connecting' : 'disconnected'
            }`} />
            <span className="text-[#a0a0a0]">
              {connectionState === 'connected' ? 'Connected' :
               connectionState === 'connecting' ? 'Connecting...' : 'Disconnected'}
            </span>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="h-14 bg-[#111] border-b border-[#2a2a2a] flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            {/* Strategy Selector (admin) or read-only label (viewer) */}
            {isAdmin ? (
              <StrategySelector compact />
            ) : (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-[#1a1a1a] border border-[#333] rounded-lg text-sm text-white min-w-[180px]">
                <Cpu size={14} className="text-blue-400" />
                <span className="truncate">
                  {activeStrategy
                    ? `${activeStrategy.strategy_id} / ${activeStrategy.preset_name}`
                    : 'No strategy loaded'}
                </span>
              </div>
            )}

            {/* Divider */}
            <div className="h-6 w-px bg-[#333]" />

            {/* What this container is actually trading right now */}
            <ActiveInstrumentBadge />

            {/* Divider */}
            <div className="h-6 w-px bg-[#333]" />

            {/* Strategy Status */}
            <div className={`status-indicator ${strategyStatus}`}>
              <span className={`w-2 h-2 rounded-full bg-current ${strategyStatus === 'running' ? 'pulse' : ''}`} />
              <span>
                {strategyStatus === 'running'
                  ? `${runMode === 'backtest' ? 'Backtest' : 'Live'} - Running`
                  : 'Stopped'}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Emergency Close Button (admin only) */}
            {isAdmin && (
              <button
                onClick={emergencyClose}
                className="btn btn-danger flex items-center gap-2"
              >
                <AlertTriangle size={16} />
                <span>Emergency Close</span>
              </button>
            )}

            {/* Power Button (admin only) */}
            {isAdmin && (
              <button
                className={`btn ${strategyStatus === 'running' ? 'btn-outline' : 'btn-success'}`}
              >
                <Power size={16} />
              </button>
            )}

            {/* User & Logout */}
            {authEnabled && username && (
              <>
                <div className="h-6 w-px bg-[#333]" />
                <div className="flex items-center gap-2 text-sm text-[#a0a0a0]">
                  <User size={14} />
                  <span>{username}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#222] text-[#666]">
                    {role}
                  </span>
                </div>
                <button
                  onClick={logout}
                  className="btn btn-outline flex items-center gap-1.5 text-sm"
                  title="Sign out"
                >
                  <LogOut size={14} />
                  <span>Logout</span>
                </button>
              </>
            )}
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-6">
          {children}
        </main>
      </div>
    </div>
  )
}
